import { Link } from "react-router-dom";
import { useBlogPosts } from "@/hooks/useBlogPosts";
import { Badge } from "@/components/ui/badge";
import { Calendar, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import type { BlogPost } from "@/lib/blog-types";

interface SuggestedPostsProps {
  currentPost: BlogPost;
}

export function SuggestedPosts({ currentPost }: SuggestedPostsProps) {
  const { data: posts } = useBlogPosts();

  if (!posts || posts.length < 2) return null;

  // Score posts by tag overlap, exclude current
  const candidates = posts
    .filter((p) => p.id !== currentPost.id)
    .map((p) => {
      const sharedTags = p.tags.filter((t: string) => currentPost.tags.includes(t)).length;
      return { post: p, score: sharedTags };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 2);

  if (candidates.length === 0) return null;

  return (
    <div className="mt-16 pt-8 border-t border-border">
      <h3 className="text-xl font-semibold mb-6 text-center">You might also enjoy</h3>
      <div className="grid sm:grid-cols-2 gap-6">
        {candidates.map(({ post }) => (
          <Link
            key={post.id}
            to={`/blog/${post.slug}`}
            className="group rounded-xl border bg-card/60 backdrop-blur-sm overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
          >
            {post.cover_image_url ? (
              <div className="h-40 overflow-hidden bg-muted">
                <img
                  src={post.cover_image_url}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
            ) : (
              <div className="h-40 bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center">
                <span className="text-4xl font-display font-bold text-primary/20">📝</span>
              </div>
            )}
            <div className="p-5">
              <div className="flex flex-wrap gap-1.5 mb-2">
                {post.tags.slice(0, 2).map((tag: string) => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
              <h4 className="font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                {post.title}
              </h4>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{post.excerpt}</p>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                {post.published_at && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {format(new Date(post.published_at), "MMM d, yyyy")}
                  </span>
                )}
                <span className="flex items-center gap-1 text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                  Read more <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
